import React, { useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { Button, CloseButton } from '../button';
import {
  IconArrowChevronRight,
  IconCircleInfoFill,
  IconCircleWarningFill,
  IconCircleCheckFill,
} from '../icons/generated';
import { notification_kind_css, notification_hide_animation } from './styles';
import type { NotificationKind, NotificationPositionType } from './types';
import { useProviderConfig } from '../provider';

export interface NotificationComponentProps {
  /**
   * id값을 지정할 수 있습니다.
   *  - id값은 unique 해야합니다.
   *  - 해당 id값으로 destroy 수동 처리가 가능합니다.
   *  - 지정하지 않을 경우 messageManager generateMessageId를 통해 자동으로 생성되어 관리됩니다.
   */
  id?: string;
  /** @default 'info' */
  kind?: NotificationKind;
  title: string;
  content: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /**
   * 하단 좌측 링크 텍스트
   */
  linkText?: string;
  /**
   * 닫힘 버튼 사용 유무
   * @default false
   */
  closeButton?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClickLink?: () => void;
  destroy: () => void;
  position: NotificationPositionType;
}

export const NotificationComponent: React.FC<NotificationComponentProps> = React.memo(
  ({
    kind = 'info',
    title,
    content,
    confirmText,
    cancelText,
    linkText,
    closeButton = false,
    onConfirm,
    onCancel,
    onClose,
    onClickLink,
    destroy,
    position,
  }) => {
    const { locale } = useProviderConfig();
    const [show, setShow] = useState(true);
    const hasAction = confirmText || cancelText;

    const close = () => {
      setShow(false);
      destroy();
      onClose?.();
    };

    const handleConfirm = () => {
      onConfirm?.();
      close();
    };

    const handleCancel = () => {
      onCancel?.();
      close();
    };

    const renderToIcon = () => {
      const color = notification_kind_css[kind];
      const size = 20;
      return (
        <>
          {kind === 'info' && <IconCircleInfoFill size={size} color={color} aria-label={locale?.accessibility.info} />}
          {kind === 'success' && (
            <IconCircleCheckFill size={size} color={color} aria-label={locale?.accessibility.success} />
          )}
          {(kind === 'error' || kind === 'warning') && (
            <IconCircleWarningFill
              size={size}
              color={color}
              aria-label={kind === 'error' ? locale?.accessibility.error : locale?.accessibility.warning}
            />
          )}
        </>
      );
    };

    return (
      <>
        <Container direction='column' visible={show} position={position} gap={32} p={24} role='status' as='section'>
          <Stack direction='column' gap={8}>
            <Stack direction='row' gap={8} align='center'>
              {renderToIcon()}
              <BaseText kind='Heading_17_Bold' color={notification_kind_css[kind]} as='h1'>
                {title}
              </BaseText>
              {closeButton && (
                <Stack direction='row' ml='auto'>
                  <CloseButton onClick={close} />
                </Stack>
              )}
            </Stack>
            {content}
          </Stack>
          <Stack direction='row' justify={hasAction ? 'space-between' : undefined} align='center'>
            {linkText && (
              <LinkText direction='row' gap={2} align='center' onClick={onClickLink} role='button' tabIndex={0}>
                <BaseText kind='Body_12_SemiBold' color={semantic_colors.content.secondary}>
                  {linkText}
                </BaseText>
                <IconArrowChevronRight color={semantic_colors.content.secondary} aria-hidden size={12} />
              </LinkText>
            )}
            {hasAction && (
              <Stack direction='row' gap={4} ml='auto'>
                {cancelText && <Button onClick={handleCancel}>{cancelText}</Button>}
                {confirmText && (
                  <Button kind='primary' onClick={handleConfirm}>
                    {confirmText}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Container>
      </>
    );
  },
  (prevProps: NotificationComponentProps, nextProps: NotificationComponentProps) => {
    return prevProps.id === nextProps.id;
  },
);

const Container = styled(Stack)<{ visible: boolean; position: NotificationPositionType }>`
  width: 100%;
  max-width: 380px;
  background-color: ${semantic_colors.background.elevated};
  color: ${semantic_colors.content.primary};
  ${text_styles.Body_13_Regular};
  ${shapes.border_radius.small};
  ${shapes.shadow.medium};
  word-break: break-all;
  ${({ visible, position }) =>
    visible &&
    css`
      animation: ${notification_hide_animation(position)} ease-in-out 0.2s forwards;
    `}
`;

// TODO(@olive-sally): linkText => TextButton 추가후 대체
const LinkText = styled(Stack)`
  cursor: pointer;
`;
